"use client";

import React from "react";
import { Link } from "@/i18n/routing";
import { RESOURCE_FAMILY_ICONS } from "@/components/resource/search/family-meta";
import type { ResourceSearchResultItem } from "@/lib/fetch/resource-search";
import type { ResourceSearchData } from "@/lib/types/resource";
import {
  ArrowUpRight,
  BookOpen,
  Building2,
  Languages,
  LayoutList,
  MapPin,
  Scale,
  ShieldCheck,
  Star,
  Tag,
  Wind,
  type LucideIcon,
} from "lucide-react";
import { useFormatter, useTranslations } from "next-intl";

type Props = {
  item: ResourceSearchResultItem;
};

type MetaChip = {
  key: string;
  icon: LucideIcon;
  label: string;
};

const ResourceSearchCard: React.FC<Props> = ({ item }) => {
  const t = useTranslations("resource-search");
  const tLocationType = useTranslations("location-type");
  const tGlossary = useTranslations("glossary");
  const tPlatform = useTranslations("platform");
  const tResource = useTranslations("resource");
  const tInsurance = useTranslations("insurance");
  const formatter = useFormatter();

  const Icon = RESOURCE_FAMILY_ICONS[item.family];

  const buildMetaChips = (data: ResourceSearchData): MetaChip[] => {
    const chips: MetaChip[] = [];
    const push = (key: string, icon: LucideIcon, label: string | null) => {
      if (label) {
        chips.push({ key, icon, label });
      }
    };

    switch (data.kind) {
      case "ARTICLE":
        push(
          "category",
          Tag,
          tResource(`category.${data.articleCategory}.short`),
        );
        push("location", MapPin, data.location?.name ?? null);
        break;
      case "GLOSSARY_TERM":
        push("type", Tag, tGlossary(`category.${data.termType}`));
        push("dutch", Languages, data.dutchTitle);
        break;
      case "HANDBOOK":
        push(
          "chapters",
          BookOpen,
          t("view.meta.chapters", { count: data.chapterCount }),
        );
        push("location", MapPin, data.location?.name ?? null);
        break;
      case "HANDBOOK_CHAPTER":
        push(
          "chapter",
          BookOpen,
          t("view.meta.chapter", { number: data.chapterOrder }),
        );
        push("location", MapPin, data.location?.name ?? null);
        break;
      case "LOCATION_PROVINCE":
      case "LOCATION_CITY":
      case "LOCATION_BOROUGH":
        push("type", Tag, tLocationType(data.location.type));
        push("parent", MapPin, data.parent?.name ?? null);
        break;
      case "MARKET_METRIC":
      case "MARKET_METRIC_LOCATION":
      case "MARKET_REPORT":
      case "MARKET_REPORT_LOCATION":
        push("group", Tag, t(`view.meta.market-group.${data.group}`));
        push("location", MapPin, data.location?.name ?? null);
        break;
      case "PLATFORM": {
        push("type", Tag, tPlatform(`type.${data.platformType}`));
        const listings = data.attributes?.totalListings;
        if (listings) {
          push(
            "listings",
            LayoutList,
            t("view.meta.listings", {
              count: formatter.number(listings),
            }),
          );
        }
        const score = data.attributes?.reviewsScore;
        const reviews = data.attributes?.reviewsAmount;
        if (score && reviews) {
          push(
            "reviews",
            Star,
            t("view.meta.reviews", {
              score: formatter.number(score, {
                maximumFractionDigits: 1,
              }),
              count: formatter.number(reviews),
            }),
          );
        }
        break;
      }
      case "PLATFORM_COMPARISON":
        push(
          "platforms",
          Scale,
          t("view.meta.platforms", { count: data.platformIds.length }),
        );
        break;
      case "PLATFORM_CITY":
      case "PLATFORM_PROVINCE":
        push(
          "platforms",
          Building2,
          t("view.meta.platforms", { count: data.platformCount }),
        );
        push("type", Tag, tLocationType(data.location.type));
        break;
      case "INSURANCE_PROVIDER":
        push(
          "products",
          ShieldCheck,
          t("view.meta.products", { count: data.productCount }),
        );
        push(
          "types",
          Tag,
          t("view.meta.insurance-types", { count: data.typeCount }),
        );
        break;
      case "INSURANCE_PRODUCT":
        push("type", ShieldCheck, tInsurance(`type.${data.insuranceType}`));
        push("kind", Tag, tInsurance(`kind.${data.productKind}`));
        break;
      case "AIR_QUALITY_COMPONENT":
        push("formula", Wind, data.formula);
        push("unit", Tag, t(`view.meta.unit.${data.unit}`));
        break;
      case "AIR_QUALITY_STATION":
        push("type", Tag, t(`view.meta.station-type.${data.stationType}`));
        push("municipality", MapPin, data.municipality ?? data.city?.name ?? null);
        break;
      case "AIR_QUALITY_CITY":
        push(
          "stations",
          Wind,
          t("view.meta.stations", { count: data.stationCount }),
        );
        break;
    }

    return chips.slice(0, 3);
  };

  const metaChips = buildMetaChips(item.data);
  const subtitle =
    item.kind === "PLATFORM" ||
    item.kind === "PLATFORM_COMPARISON" ||
    item.kind === "HANDBOOK_CHAPTER" ||
    item.kind === "INSURANCE_PRODUCT" ||
    item.kind === "INSURANCE_PROVIDER"
      ? item.cardSubtitle
      : null;

  return (
    <Link
      href={item.path}
      title={item.linkTitle}
      className="google-anno-skip group flex h-full flex-col rounded-2xl border border-border bg-background shadow-sm transition-all hover:border-zinc-300 hover:shadow-md"
    >
      <div className="flex flex-1 flex-col gap-3 p-5">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="flex size-10 flex-none items-center justify-center rounded-xl bg-secondary">
            <Icon className="size-5" />
          </span>
          <span className="truncate text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {t(`view.kinds.${item.kind}`)}
          </span>
        </div>

        <div className="flex flex-col gap-1.5">
          <h3 className="line-clamp-2 text-base font-semibold leading-snug">
            {item.shortLabel}
          </h3>
          {subtitle && (
            <p className="line-clamp-1 text-xs font-medium text-muted-foreground">
              {subtitle}
            </p>
          )}
          <p className="line-clamp-3 text-sm text-muted-foreground">
            {item.cardDescription}
          </p>
        </div>

        {metaChips.length > 0 && (
          <div className="mt-auto flex flex-wrap gap-1.5 pt-1">
            {metaChips.map((chip) => (
              <span
                key={chip.key}
                className="inline-flex max-w-full items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-[11px] font-medium leading-none text-muted-foreground"
              >
                <chip.icon className="size-3 flex-none" />
                <span className="truncate">{chip.label}</span>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-border px-5 py-3">
        <span className="flex items-center gap-1 text-sm font-semibold text-blue-700 group-hover:underline">
          {t(`view.open.${item.kind}`)}
          <ArrowUpRight className="size-4 flex-none transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </span>
        <span className="flex-none text-xs text-muted-foreground">
          {t("view.updated", {
            date: formatter.dateTime(new Date(item.updatedAt), {
              month: "short",
              year: "numeric",
            }),
          })}
        </span>
      </div>
    </Link>
  );
};

export default ResourceSearchCard;
